import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Outlet } from 'react-router-dom'
import { SplitFactoryProvider } from '@splitsoftware/splitio-react'

import { selectCurrentUser } from '@/app/slices/appSlice'
import { useIntercomPageTracking } from '@/common/hooks/useIntercomPageTracking'
import { usePostHogIdentity } from '@/common/hooks/usePostHogIdentity'

const SplitProviderWrapper = () => {
    const currentUser = useSelector(selectCurrentUser)

    // QM-3097: log a page view to Intercom on every route change so Intercom's
    // page-view count tracks Userflow. Safe to mount here — this wrapper is the
    // topmost element of the router tree, so it's inside both the router (for
    // useLocation) and IntercomProvider (for useIntercom).
    useIntercomPageTracking()

    // QM-3550: this is the only element every route renders under (see the
    // FME config below), so it's also the right place to resolve PostHog
    // identity before any flag-gated component reads useFeatureFlags.
    usePostHogIdentity()

    // Derive Split configuration from current user - memoized to prevent unnecessary re-renders
    const splitConfig = useMemo(
        () => ({
            core: {
                authorizationKey: import.meta.env.VITE_SPLIT_APIKEY,
                key: currentUser?.emailAddress || 'anonymous',
                trafficType: currentUser ? 'account' : 'anonymous',
            },
        }),
        [currentUser]
    )

    return (
        <SplitFactoryProvider config={splitConfig}>
            <Outlet />
        </SplitFactoryProvider>
    )
}

export default SplitProviderWrapper
